package com.shadowings.gero.data.lessons.chapter11

import com.shadowings.gero.data.lessons.frenchClause
import com.shadowings.gero.data.lessons.moveGermanSeparableParticleToEnd
import com.shadowings.gero.data.lessons.inlineMeaning
import com.shadowings.gero.data.lessons.DistractorPool
import com.shadowings.gero.data.lessons.anyResolvable
import com.shadowings.gero.data.lessons.chapter6.conjugateVerbDe
import com.shadowings.gero.data.lessons.chapter6.conjugateVerbEn
import com.shadowings.gero.data.lessons.chapter6.conjugateVerbEs
import com.shadowings.gero.data.lessons.chapter6.conjugateVerbFr
import com.shadowings.gero.data.lessons.chapter6.conjugateVerbIt
import com.shadowings.gero.data.lessons.formatEnglishComplexObject
import com.shadowings.gero.data.lessons.formatEnglishDemonstrativeSubject
import com.shadowings.gero.data.lessons.formatEnglishObjectPhrase
import com.shadowings.gero.data.lessons.formatEnglishSubject
import com.shadowings.gero.data.lessons.formatFrenchComplexObject
import com.shadowings.gero.data.lessons.formatFrenchDemonstrativeSubject
import com.shadowings.gero.data.lessons.formatFrenchSubject
import com.shadowings.gero.data.lessons.formatGermanComplexObject
import com.shadowings.gero.data.lessons.formatGermanDemonstrativeSubject
import com.shadowings.gero.data.lessons.formatGermanObject
import com.shadowings.gero.data.lessons.formatGermanSubject
import com.shadowings.gero.data.lessons.formatItalianComplexObject
import com.shadowings.gero.data.lessons.formatItalianDemonstrativeSubject
import com.shadowings.gero.data.lessons.formatItalianSubject
import com.shadowings.gero.data.lessons.formatSpanishComplexObject
import com.shadowings.gero.data.lessons.formatSpanishDemonstrativeSubject
import com.shadowings.gero.data.lessons.formatSpanishObject
import com.shadowings.gero.data.lessons.animalObjectIsThing
import com.shadowings.gero.data.lessons.isHumanSubject
import com.shadowings.gero.data.lessons.isCommonLivingNoun
import com.shadowings.gero.data.lessons.formatSpanishSubject
import com.shadowings.gero.data.lessons.getDemonstrativeGardiner
import com.shadowings.gero.data.lessons.getDemonstrativeReading
import com.shadowings.gero.data.lessons.hasVerbKey
import com.shadowings.gero.data.lessons.isItForcePresent
import com.shadowings.gero.data.lessons.isStativeVerb
import com.shadowings.gero.data.lessons.joinListDe
import com.shadowings.gero.data.lessons.joinListEn
import com.shadowings.gero.data.lessons.joinListEs
import com.shadowings.gero.data.lessons.joinListFr
import com.shadowings.gero.data.lessons.joinListIt
import com.shadowings.gero.data.lessons.pickDistinctIdsByBaseWord
import com.shadowings.gero.data.lessons.pickPossessivePronoun
import com.shadowings.gero.data.lessons.possessiveTranslation
import com.shadowings.gero.data.lessons.possessiveGenitiveTranslation
import com.shadowings.gero.data.lessons.withPossessableObjects
import com.shadowings.gero.data.lessons.withPossessablePossessors
import com.shadowings.gero.data.lessons.withPossessableSubjects
import com.shadowings.gero.data.lessons.tokenizePhrase
import com.shadowings.gero.data.repository.DatabaseRepositoryImpl
import com.shadowings.gero.data.repository.PossessivePairs
import com.shadowings.gero.data.repository.AdjectivePairs
import com.shadowings.gero.data.lessons.adjectiveAlternatives
import com.shadowings.gero.data.lessons.adjectiveHieroglyphs
import com.shadowings.gero.data.lessons.adjectivePhraseTranslation
import com.shadowings.gero.data.lessons.adjectiveTransliteration
import com.shadowings.gero.data.lessons.pickSentenceAdjective
import com.shadowings.gero.data.repository.SentenceOptions
import com.shadowings.gero.domain.model.IQuizContent
import com.shadowings.gero.domain.model.Option
import com.shadowings.gero.domain.model.PhoneticKeyboardContent
import com.shadowings.gero.domain.model.QuizText
import com.shadowings.gero.domain.model.SingleSelectionContent
import com.shadowings.gero.domain.model.TextTranslationContent
import com.shadowings.gero.domain.model.WordSelectionContent
import com.shadowings.gero.presentation.model.Verb
import com.shadowings.gero.presentation.model.Word
import gero.composeapp.generated.resources.Res
import gero.composeapp.generated.resources.quiz_ripasso_cosa_significa_gero
import gero.composeapp.generated.resources.quiz_ripasso_quale_gero_significa
import gero.composeapp.generated.resources.quiz_ripasso_traslitterazione
import kotlin.random.Random

data class PseudoverbalSentence(
    val hieroglyphs: String,
    val transliteration: String,
    val translationIt: String,
    val translationEn: String,
    val translationDe: String,
    val translationEs: String,
    val translationFr: String,
    val isPronominal: Boolean,
    val subjectKey: String,
    val verbKey: String,
    val isNegative: Boolean = false,
    val thisSubject: Boolean = false
) {
    fun getTranslation(languageCode: String): String = when (languageCode) {
        "it" -> translationIt
        "de" -> translationDe
        "es" -> translationEs
        "fr" -> translationFr
        else -> translationEn
    }
}

data class PseudoverbalObjectSentence(
    val hieroglyphs: String,
    val transliteration: String,
    val translationIt: String,
    val translationEn: String,
    val translationDe: String,
    val translationEs: String,
    val translationFr: String,
    val isPronominal: Boolean,
    val subjectKeys: List<String>,
    val verbKey: String,
    val objectKeys: List<String>,
    val possessorKey: String? = null,
    val isDirectGenitive: Boolean = false,
    val isNegative: Boolean = false,
    val thisSubject: Boolean = false,
    // Same as SentenceResult: the other right word orders when there's an adjective.
    val alternativeTranslations: Map<String, List<String>> = emptyMap(),
    val hasAdjective: Boolean = false
) {
    fun getTranslation(languageCode: String): String = when (languageCode) {
        "it" -> translationIt
        "de" -> translationDe
        "es" -> translationEs
        "fr" -> translationFr
        else -> translationEn
    }
}

data class PseudoverbalComplexObjectSentence(
    val hieroglyphs: String,
    val transliteration: String,
    val translationIt: String,
    val translationEn: String,
    val translationDe: String,
    val translationEs: String,
    val translationFr: String,
    val isPronominal: Boolean,
    val isDirectGenitive: Boolean,
    val subjectKey: String,
    val verbKey: String,
    val possessedKey: String,
    val possessorKey: String,
    val isNegative: Boolean = false,
    val thisSubject: Boolean = false
) {
    fun getTranslation(languageCode: String): String = when (languageCode) {
        "it" -> translationIt
        "de" -> translationDe
        "es" -> translationEs
        "fr" -> translationFr
        else -> translationEn
    }
}




// Used when `words` (e.g. a "seen words" filtered subset) has no pronoun entries at
// all - keeps the 3 pseudoverbal builders below from crashing on an empty pronoun pool.
internal val FALLBACK_IO_WORD = Word(
    id = "IO",
    meaning = "Io",
    reading = ".i",
    gardiner = "A1",
    note = "",
    alt1 = null,
    alt2 = null
)

internal fun pickPronounOrFallback(words: List<Word>, random: Random): Word {
    val availablePronouns = words.filter { it.isPronoun }
    return if (availablePronouns.isNotEmpty()) availablePronouns.random(random) else FALLBACK_IO_WORD
}

internal fun getGerundIt(verbKey: String): String = when (verbKey) {
    "MANGIARE" -> "mangiando"
    "DARE" -> "dando"
    "ASCOLTARE" -> "ascoltando"
    "DIRE" -> "dicendo"
    "BERE" -> "bevendo"
    "USCIRE" -> "uscendo"
    "ENTRARE" -> "entrando"
    "AMARE" -> "amando"
    "ESISTERE" -> "esistendo"
    "VOLARE" -> "volando"
    "COLTIVARE" -> "coltivando"
    "CACCIARE" -> "cacciando"
    "OFFRIRE" -> "offrendo"
    "VEDERE" -> "vedendo"
    "SCRIVERE" -> "scrivendo"
    "PARLARE" -> "parlando"
    "TROVARE" -> "trovando"
    "FARE" -> "facendo"
    "PORTARE" -> "portando"
    "CONOSCERE" -> "conoscendo"
    "ANDARE" -> "andando"
    "INVIARE" -> "inviando"
    "PRENDERE" -> "prendendo"
    "APRIRE" -> "aprendo"
    "GUARDARE" -> "guardando"
    "CANTARE" -> "cantando"
    "CHIAMARE" -> "chiamando"
    "SIGILLARE" -> "sigillando"
    "LAVORARE" -> "lavorando"
    "SOLLEVARE" -> "sollevando"
    "NAVIGARE" -> "navigando"
    "SPLENDERE" -> "splendendo"
    "PURIFICARE" -> "purificando"
    "ARROSTIRE" -> "arrostendo"
    else -> verbKey.lowercase()
}

internal fun getGerundEn(verbKey: String): String = when (verbKey) {
    "MANGIARE" -> "eating"
    "DARE" -> "giving"
    "ASCOLTARE" -> "listening"
    "DIRE" -> "saying"
    "BERE" -> "drinking"
    "USCIRE" -> "going out"
    "ENTRARE" -> "entering"
    "AMARE" -> "loving"
    "ESISTERE" -> "existing"
    "VOLARE" -> "flying"
    "COLTIVARE" -> "cultivating"
    "CACCIARE" -> "hunting"
    "OFFRIRE" -> "offering"
    "VEDERE" -> "seeing"
    "SCRIVERE" -> "writing"
    "PARLARE" -> "speaking"
    "TROVARE" -> "finding"
    "FARE" -> "doing"
    "PORTARE" -> "bringing"
    "CONOSCERE" -> "knowing"
    "ANDARE" -> "going"
    "INVIARE" -> "sending"
    "PRENDERE" -> "taking"
    "APRIRE" -> "opening"
    "GUARDARE" -> "looking"
    "CANTARE" -> "singing"
    "CHIAMARE" -> "calling"
    "SIGILLARE" -> "sealing"
    "LAVORARE" -> "working"
    "SOLLEVARE" -> "lifting"
    "NAVIGARE" -> "sailing"
    "SPLENDERE" -> "shining"
    "PURIFICARE" -> "purifying"
    "ARROSTIRE" -> "roasting"
    else -> verbKey.lowercase()
}

internal fun getGerundEs(verbKey: String): String = when (verbKey) {
    "MANGIARE" -> "comiendo"
    "DARE" -> "dando"
    "ASCOLTARE" -> "escuchando"
    "DIRE" -> "diciendo"
    "BERE" -> "bebiendo"
    "USCIRE" -> "saliendo"
    "ENTRARE" -> "entrando"
    "AMARE" -> "amando"
    "ESISTERE" -> "existiendo"
    "VOLARE" -> "volando"
    "COLTIVARE" -> "cultivando"
    "CACCIARE" -> "cazando"
    "OFFRIRE" -> "ofreciendo"
    "VEDERE" -> "viendo"
    "SCRIVERE" -> "escribiendo"
    "PARLARE" -> "hablando"
    "TROVARE" -> "encontrando"
    "FARE" -> "haciendo"
    "PORTARE" -> "llevando"
    "CONOSCERE" -> "conociendo"
    "ANDARE" -> "yendo"
    "INVIARE" -> "enviando"
    "PRENDERE" -> "tomando"
    "APRIRE" -> "abriendo"
    "GUARDARE" -> "mirando"
    "CANTARE" -> "cantando"
    "CHIAMARE" -> "llamando"
    "SIGILLARE" -> "sellando"
    "LAVORARE" -> "trabajando"
    "SOLLEVARE" -> "levantando"
    "NAVIGARE" -> "navegando"
    "SPLENDERE" -> "brillando"
    "PURIFICARE" -> "purificando"
    "ARROSTIRE" -> "asando"
    else -> verbKey.lowercase()
}

// French has no simple gerund-based progressive like it/en/es/de's "sta mangiando"/"is eating";
// the closest periphrastic construction is "être en train de + infinitive", with "de" eliding
// to "d'" before an infinitive that starts with a vowel sound.
internal fun getEnTrainDeFr(verbKey: String): String = when (verbKey) {
    "MANGIARE" -> "en train de manger"
    "DARE" -> "en train de donner"
    "ASCOLTARE" -> "en train d'écouter"
    "DIRE" -> "en train de dire"
    "BERE" -> "en train de boire"
    "USCIRE" -> "en train de sortir"
    "ENTRARE" -> "en train d'entrer"
    "AMARE" -> "en train d'aimer"
    "ESISTERE" -> "en train d'exister"
    "VOLARE" -> "en train de voler"
    "COLTIVARE" -> "en train de cultiver"
    "CACCIARE" -> "en train de chasser"
    "OFFRIRE" -> "en train d'offrir"
    "VEDERE" -> "en train de voir"
    "SCRIVERE" -> "en train d'écrire"
    "PARLARE" -> "en train de parler"
    "TROVARE" -> "en train de trouver"
    "FARE" -> "en train de faire"
    "PORTARE" -> "en train de porter"
    "CONOSCERE" -> "en train de connaître"
    "ANDARE" -> "en train d'aller"
    "INVIARE" -> "en train d'envoyer"
    "PRENDERE" -> "en train de prendre"
    "APRIRE" -> "en train d'ouvrir"
    "GUARDARE" -> "en train de regarder"
    "CANTARE" -> "en train de chanter"
    "CHIAMARE" -> "en train d'appeler"
    "SIGILLARE" -> "en train de sceller"
    "LAVORARE" -> "en train de travailler"
    "SOLLEVARE" -> "en train de soulever"
    "NAVIGARE" -> "en train de naviguer"
    "SPLENDERE" -> "en train de briller"
    "PURIFICARE" -> "en train de purifier"
    "ARROSTIRE" -> "en train de rôtir"
    else -> "en train de ${verbKey.lowercase()}"
}

// Stativity is a property of the verb itself, not of what the learner has
// seen: callers often pass a "seen words" subset, so fall back to the full
// bundled word list when the verb's own row isn't in it.
private fun isPseudoverbalStative(verbKey: String, words: List<Word>): Boolean =
    isStativeVerb(verbKey, words) || isStativeVerb(verbKey, DatabaseRepositoryImpl.words)

private fun isPseudoverbalItForcePresent(verbKey: String, words: List<Word>): Boolean =
    isItForcePresent(verbKey, words) || isItForcePresent(verbKey, DatabaseRepositoryImpl.words)

// Subject pronoun for a pseudoverbal translation (German is handled apart:
// it has no progressive, so its builders just conjugate the verb).
internal fun pseudoverbalPronounSubject(pronounId: String, languageCode: String): String = when (languageCode) {
    "it" -> when (pronounId) {
        "IO" -> "io"; "TU_M" -> "tu (m.)"; "TU_F" -> "tu (f.)"; "ELLA" -> "ella"; "ESSO" -> "esso"
        "NOI" -> "noi"; "VOI" -> "voi"; "ESSI" -> "essi"; else -> "egli"
    }
    "es" -> when (pronounId) {
        "IO" -> "yo"; "TU_M" -> "tú (m.)"; "TU_F" -> "tú (f.)"; "ELLA" -> "ella"; "ESSO" -> "eso"
        "NOI" -> "nosotros"; "VOI" -> "vosotros"; "ESSI" -> "ellos"; else -> "él"
    }
    "fr" -> when (pronounId) {
        "IO" -> "je"; "TU_M" -> "tu (m.)"; "TU_F" -> "tu (f.)"; "ELLA" -> "elle"; "ESSO" -> "cela"
        "NOI" -> "nous"; "VOI" -> "vous"; "ESSI" -> "ils"; else -> "il"
    }
    else -> when (pronounId) {
        "IO" -> "I"; "TU_M" -> "you (m.)"; "TU_F" -> "you (f.)"; "ELLA" -> "she"; "ESSO" -> "it"
        "NOI" -> "we"; "VOI" -> "you"; "ESSI" -> "they"; else -> "he"
    }
}

// Verb part (no subject) of a pseudoverbal iw/nn + subject + ḥr + infinitive
// translation, for it/en/es/fr. `personKey` is a pronoun id, or EGLI/ESSI for
// a nominal subject. Like chapter13's HrComplementSentenceUtils, stative verbs
// (words.tsv is_stative) never take the progressive in en/es/fr, and
// it_force_present verbs never take "stare + gerundio" in Italian.
internal fun pseudoverbalVerbPhrase(
    verbKey: String,
    personKey: String,
    languageCode: String,
    isNegative: Boolean,
    words: List<Word>
): String {
    val isStative = isPseudoverbalStative(verbKey, words)
    return when (languageCode) {
        "it" -> {
            val core = if (isPseudoverbalItForcePresent(verbKey, words)) {
                conjugateVerbIt(verbKey, personKey)
            } else {
                val aux = when (personKey) {
                    "IO" -> "sto"; "TU_M", "TU_F" -> "stai"; "NOI" -> "stiamo"; "VOI" -> "state"; "ESSI" -> "stanno"
                    else -> "sta"
                }
                "$aux ${getGerundIt(verbKey)}"
            }
            if (isNegative) "non $core" else core
        }
        "es" -> {
            val core = if (isStative) {
                conjugateVerbEs(verbKey, personKey)
            } else {
                val aux = when (personKey) {
                    "IO" -> "estoy"; "TU_M", "TU_F" -> "estás"; "NOI" -> "estamos"; "VOI" -> "estáis"; "ESSI" -> "están"
                    else -> "está"
                }
                "$aux ${getGerundEs(verbKey)}"
            }
            if (isNegative) "no $core" else core
        }
        "fr" -> {
            val core = if (isStative) {
                conjugateVerbFr(verbKey, personKey)
            } else {
                val aux = when (personKey) {
                    "IO" -> "suis"; "TU_M", "TU_F" -> "es"; "NOI" -> "sommes"; "VOI" -> "êtes"; "ESSI" -> "sont"
                    else -> "est"
                }
                "$aux ${getEnTrainDeFr(verbKey)}"
            }
            if (!isNegative) {
                core
            } else {
                val verbWord = core.substringBefore(" ")
                val rest = core.substringAfter(" ", "")
                val neg = if (verbWord.first().lowercaseChar() in "aeiouéèêh") "n'$verbWord" else "ne $verbWord"
                listOf(neg, "pas", rest).filter { it.isNotEmpty() }.joinToString(" ")
            }
        }
        else -> {
            val isThirdSingular = personKey !in listOf("IO", "TU_M", "TU_F", "NOI", "VOI", "ESSI")
            if (isStative) {
                if (isNegative) {
                    "${if (isThirdSingular) "does" else "do"} not ${conjugateVerbEn(verbKey, "ESSI")}"
                } else {
                    conjugateVerbEn(verbKey, personKey)
                }
            } else {
                val aux = when {
                    personKey == "IO" -> "am"
                    isThirdSingular -> "is"
                    else -> "are"
                }
                if (isNegative) "$aux not ${getGerundEn(verbKey)}" else "$aux ${getGerundEn(verbKey)}"
            }
        }
    }
}

// "$subject $verbPhrase", eliding French "je" before a vowel-initial verb
// ("j'aime", "j'existe") - only reachable for stative verbs, since the
// progressive always starts with "suis".
internal fun pseudoverbalPronounClause(pronounId: String, verbKey: String, languageCode: String, isNegative: Boolean, words: List<Word>): String {
    val subject = pseudoverbalPronounSubject(pronounId, languageCode)
    val verbPhrase = pseudoverbalVerbPhrase(verbKey, pronounId, languageCode, isNegative, words)
    return if (languageCode == "fr") frenchClause(subject, verbPhrase) else "$subject $verbPhrase"
}

private fun applyHieroglyphVariations(hieroglyphs: String, random: Random = Random): String {
    var result = hieroglyphs
    if (result.contains("M17-G43-V31") && random.nextBoolean()) {
        result = result.replace("M17-G43-V31", "M17-Z7:V31")
    }
    if (result.contains("M17-G43-X1") && random.nextBoolean()) {
        result = result.replace("M17-G43-X1", "M17-Z7:X1")
    }
    if (result.contains("M17-G43-I9") && random.nextBoolean()) {
        result = result.replace("M17-G43-I9", "M17-Z7:I9")
    }
    return result
}

internal fun buildPronominalPseudoverbalSentence(
    verb: Verb,
    pronounWord: Word,
    random: Random = Random,
    isNegative: Boolean = false,
    words: List<Word> = DatabaseRepositoryImpl.words
): PseudoverbalSentence {
    val verbKey = verb.chiaveWords
    val pronounHiero = pronounWord.gardiner
    val pronounTrans = pronounWord.reading
    val infHiero = verb.formaInfinita.ifBlank { verb.formaBaseSenzaDet }
    val infTrans = verb.traslitterazioneInfinita.ifBlank { verb.traslitterazione }

    val pronTrans = if (pronounTrans.startsWith(".")) pronounTrans else ".$pronounTrans"
    val rawHieroglyphs = if (isNegative) "D35:N35-${pronounHiero}-D2h2:Z1-${infHiero}" else "M17-G43-${pronounHiero}-D2h2:Z1-${infHiero}"
    val hieroglyphs = if (isNegative) rawHieroglyphs else applyHieroglyphVariations(rawHieroglyphs, random)
    val transliteration = if (isNegative) "nn$pronTrans ḥr ${infTrans}" else "iw$pronTrans ḥr ${infTrans}"
    val translationIt = pseudoverbalPronounClause(pronounWord.id, verbKey, "it", isNegative, words)
    val translationEn = pseudoverbalPronounClause(pronounWord.id, verbKey, "en", isNegative, words)
    val translationEs = pseudoverbalPronounClause(pronounWord.id, verbKey, "es", isNegative, words)

    val verbDe = conjugateVerbDe(verb.chiaveWords, pronounWord.id)
    val translationDe = moveGermanSeparableParticleToEnd(when (pronounWord.id) {
        "IO" -> "ich $verbDe"
        "TU_M" -> "du (m.) $verbDe"
        "TU_F" -> "du (f.) $verbDe"
        "EGLI" -> "er $verbDe"
        "ELLA" -> "sie $verbDe"
        "ESSO" -> "es $verbDe"
        "NOI" -> "wir $verbDe"
        "VOI" -> "ihr $verbDe"
        "ESSI" -> "sie $verbDe"
        else -> "er $verbDe"
    } + if (isNegative) " nicht" else "")
    val translationFr = pseudoverbalPronounClause(pronounWord.id, verbKey, "fr", isNegative, words)

    return PseudoverbalSentence(
        hieroglyphs = hieroglyphs,
        transliteration = transliteration,
        translationIt = translationIt,
        translationEn = translationEn,
        translationDe = translationDe,
        translationEs = translationEs,
        translationFr = translationFr,
        isPronominal = true,
        subjectKey = pronounWord.id,
        verbKey = verb.chiaveWords,
        isNegative = isNegative
    )
}

internal fun buildNounPseudoverbalSentence(
    verb: Verb,
    subjectWord: Word,
    includeIw: Boolean = true,
    isNegative: Boolean = false,
    thisSubject: Boolean = false,
    words: List<Word> = DatabaseRepositoryImpl.words
): PseudoverbalSentence {
    val verbKey = verb.chiaveWords
    val subjHiero = if (thisSubject) "${subjectWord.gardiner}-${getDemonstrativeGardiner(subjectWord)}" else subjectWord.gardiner
    val subjTrans = if (thisSubject) "${subjectWord.reading} ${getDemonstrativeReading(subjectWord)}" else subjectWord.reading
    val infHiero = verb.formaInfinita.ifBlank { verb.formaBaseSenzaDet }
    val infTrans = verb.traslitterazioneInfinita.ifBlank { verb.traslitterazione }

    val hieroglyphs =
        if (isNegative) "D35:N35-${subjHiero}-D2h2:Z1-${infHiero}"
        else if (includeIw) "M17-G43-${subjHiero}-D2h2:Z1-${infHiero}"
        else "${subjHiero}-D2h2:Z1-${infHiero}"
    val transliteration =
        if (isNegative) "nn ${subjTrans} ḥr ${infTrans}"
        else if (includeIw) "iw ${subjTrans} ḥr ${infTrans}"
        else "${subjTrans} ḥr ${infTrans}"
    val subjStrIt = if (thisSubject) formatItalianDemonstrativeSubject(subjectWord) else formatItalianSubject(subjectWord)
    val verbStrIt = pseudoverbalVerbPhrase(verbKey, if (subjectWord.isPlural) "ESSI" else "EGLI", "it", isNegative, words)
    val translationIt = "$subjStrIt $verbStrIt"
    val subjStrEn = if (thisSubject) formatEnglishDemonstrativeSubject(subjectWord) else formatEnglishSubject(subjectWord)
    val verbStrEn = pseudoverbalVerbPhrase(verbKey, if (subjectWord.isPlural) "ESSI" else "EGLI", "en", isNegative, words)
    val translationEn = "$subjStrEn $verbStrEn"
    val subjStrEs = if (thisSubject) formatSpanishDemonstrativeSubject(subjectWord) else formatSpanishSubject(subjectWord)
    val verbStrEs = pseudoverbalVerbPhrase(verbKey, if (subjectWord.isPlural) "ESSI" else "EGLI", "es", isNegative, words)
    val translationEs = "$subjStrEs $verbStrEs"

    val subjStrDe = if (thisSubject) formatGermanDemonstrativeSubject(subjectWord) else formatGermanSubject(subjectWord)
    val pronounCode = if (subjectWord.isPlural) "ESSI" else "EGLI"
    val verbDe = conjugateVerbDe(verb.chiaveWords, pronounCode)
    val translationDe = moveGermanSeparableParticleToEnd(if (isNegative) "$subjStrDe $verbDe nicht" else "$subjStrDe $verbDe")
    val subjStrFr = if (thisSubject) formatFrenchDemonstrativeSubject(subjectWord) else formatFrenchSubject(subjectWord)
    val verbStrFr = pseudoverbalVerbPhrase(verbKey, if (subjectWord.isPlural) "ESSI" else "EGLI", "fr", isNegative, words)
    val translationFr = "$subjStrFr $verbStrFr"

    return PseudoverbalSentence(
        hieroglyphs = hieroglyphs,
        transliteration = transliteration,
        translationIt = translationIt,
        translationEn = translationEn,
        translationDe = translationDe,
        translationEs = translationEs,
        translationFr = translationFr,
        isPronominal = false,
        subjectKey = subjectWord.id,
        verbKey = verb.chiaveWords,
        isNegative = isNegative,
        thisSubject = thisSubject
    )
}

internal fun getRandomPseudoverbalSentence(
    verbs: List<Verb>,
    words: List<Word>,
    isPronominal: Boolean? = null,
    random: Random = Random,
    verbSentences: List<SentenceOptions> = DatabaseRepositoryImpl.verbSentences,
    isNegative: Boolean = false,
    thisSubject: Boolean = false
): PseudoverbalSentence {
    val effectiveSentences = verbSentences.ifEmpty { DatabaseRepositoryImpl.verbSentences }

    val patterns = effectiveSentences.mapNotNull { row ->
        val verbKey = row.verbs.firstOrNull() ?: return@mapNotNull null
        if (verbs.hasVerbKey(verbKey)) Pair(row.subjects, verbKey) else null
    }

    val pronominal = if (thisSubject) false else (isPronominal ?: random.nextBoolean())

    return if (pronominal) {
        val humanPatterns = patterns.filter { (subjects, _) ->
            subjects.any { isHumanSubject(it, words) }
        }
        val chosenVerbKey = if (humanPatterns.isNotEmpty()) {
            humanPatterns.random(random).second
        } else if (patterns.isNotEmpty()) {
            patterns.random(random).second
        } else {
            verbs.random(random).chiaveWords
        }
        val verb = verbs.firstOrNull { it.chiaveWords == chosenVerbKey } ?: verbs.first()

        val pronounWord = pickPronounOrFallback(words, random)
        buildPronominalPseudoverbalSentence(verb, pronounWord, random, isNegative = isNegative, words = words)
    } else {
        val nounPatterns = patterns.filter { (subjects, _) ->
            subjects.any { id -> words.any { it.id == id && !it.isPronoun } }
        }

        val (patternSubjects, chosenVerbKey) = if (nounPatterns.isNotEmpty()) {
            nounPatterns.random(random)
        } else if (patterns.isNotEmpty()) {
            patterns.random(random)
        } else {
            Pair(emptyList<String>(), verbs.random(random).chiaveWords)
        }

        val verb = verbs.firstOrNull { it.chiaveWords == chosenVerbKey } ?: verbs.first()
        val availableNounsInPattern = patternSubjects.mapNotNull { id ->
            words.find { it.id == id && !it.isPronoun }
        }

        val nounWord = if (availableNounsInPattern.isNotEmpty()) {
            availableNounsInPattern.random(random)
        } else {
            val availableNouns = words.filter { it.isCommonLivingNoun() }
            if (availableNouns.isNotEmpty()) {
                availableNouns.random(random)
            } else {
                words.firstOrNull { !it.isPronoun } ?: words.first()
            }
        }

        buildNounPseudoverbalSentence(verb, nounWord, isNegative = isNegative, thisSubject = thisSubject, words = words)
    }
}

internal fun buildExplicitPseudoverbalObjectSentence(
    verb: Verb,
    subjects: List<Word>,
    objects: List<Word>,
    isPronominal: Boolean,
    possessor: Word? = null,
    isDirect: Boolean? = null,
    random: Random = Random,
    isNegative: Boolean = false,
    thisSubject: Boolean = false,
    words: List<Word> = DatabaseRepositoryImpl.words,
    // Suffix pronoun owning the first nominal subject (iw sꜣ.i ḥr wnm t "mio figlio sta mangiando
    // il pane"). Not with a pronominal subject or the demonstrative.
    subjectPossessive: Word? = null,
    // Suffix pronoun owning the first object (iw sš ḥr wnm t.f "lo scriba sta mangiando il suo
    // pane"). Not with a possessor, which takes its place.
    objectPossessive: Word? = null,
    // Suffix pronoun owning the possessor (iw sn ḥr wnm dqrw n sš.ṯn "il fratello sta mangiando il
    // frutto del vostro scriba").
    possessorPossessive: Word? = null,
    // Adjective after the first nominal subject / the first object (iw sꜣ nfr ḥr wnm t "il figlio
    // buono sta mangiando il pane"). Never on a noun with a possessive, the demonstrative or a possessor.
    subjectAdjective: Word? = null,
    objectAdjective: Word? = null
): PseudoverbalObjectSentence {
    require(possessor == null || objects.isNotEmpty()) { "possessor richiede almeno un oggetto (il posseduto)" }
    require(subjectAdjective == null || (!isPronominal && !thisSubject && subjectPossessive == null)) {
        "aggettivo e possessivo sullo stesso soggetto"
    }
    require(objectAdjective == null || (objects.isNotEmpty() && possessor == null && objectPossessive == null)) {
        "aggettivo e possessivo sullo stesso oggetto"
    }

    val verbKey = verb.chiaveWords
    val animalIsThing = animalObjectIsThing(verbKey, words)
    val infHiero = verb.formaInfinita.ifBlank { verb.formaBaseSenzaDet }
    val infTrans = verb.traslitterazioneInfinita.ifBlank { verb.traslitterazione }

    // When there's a possessor, the LAST object is the one it possesses
    // ("il gatto e il campo DEL PASTORE") — every other object stays plain.
    val leadingObjects = if (possessor != null) objects.dropLast(1) else objects
    val possessedObject = if (possessor != null) objects.last() else null
    val genitive = if (possessedObject != null && possessor != null) {
        determineGenitiveDetails(possessedObject, possessor, isDirect, random)
    } else null
    val ownerPossessive = possessorPossessive?.takeIf { possessor != null }
    val leadingPossessive = objectPossessive?.takeIf { possessor == null }

    val possessorHiero = possessor?.let { if (ownerPossessive != null) "${it.gardiner}-${ownerPossessive.gardiner}" else it.gardiner }
    val lastObjectHiero = if (possessedObject != null && possessor != null && genitive != null) {
        if (genitive.first) "${possessedObject.gardiner}-$possessorHiero"
        else "${possessedObject.gardiner}${genitive.second.first}$possessorHiero"
    } else null
    val objectsHiero = (leadingObjects.mapIndexed { index, word ->
        if (index == 0 && leadingPossessive != null) "${word.gardiner}-${leadingPossessive.gardiner}"
        else if (index == 0 && objectAdjective != null) adjectiveHieroglyphs(word, objectAdjective)
        else word.gardiner
    } + listOfNotNull(lastObjectHiero)).joinToString("-")

    val possessorTrans = possessor?.let { if (ownerPossessive != null) "${it.reading}.${ownerPossessive.reading}" else it.reading }
    val lastObjectTrans = if (possessedObject != null && possessor != null && genitive != null) {
        if (genitive.first || genitive.second.second.isBlank()) "${possessedObject.reading} $possessorTrans"
        else "${possessedObject.reading} ${genitive.second.second} $possessorTrans"
    } else null
    val objectsTrans = (leadingObjects.mapIndexed { index, word ->
        if (index == 0 && leadingPossessive != null) "${word.reading}.${leadingPossessive.reading}"
        else if (index == 0 && objectAdjective != null) adjectiveTransliteration(word, objectAdjective)
        else word.reading
    } + listOfNotNull(lastObjectTrans)).joinToString(" ")

    // The plain objects, the first with its suffix; the possessed one with the noun genitive, or
    // with the owner's own possessive ("l'acqua di tuo padre").
    fun objectsIn(languageCode: String, plain: (Word) -> String, complex: (Word, Word) -> String): List<String> =
        leadingObjects.mapIndexed { index, word ->
            if (index == 0 && leadingPossessive != null) possessiveTranslation(word, leadingPossessive.id, languageCode, asObject = true, animalIsThing = animalIsThing)
            else if (index == 0 && objectAdjective != null) adjectivePhraseTranslation(word, objectAdjective, languageCode, asObject = true, animalIsThing = animalIsThing).first()
            else plain(word)
        } + listOfNotNull(possessedObject?.let {
            if (ownerPossessive != null) "${plain(it)} ${possessiveGenitiveTranslation(possessor!!, ownerPossessive.id, languageCode)}"
            else complex(it, possessor!!)
        })
    val objectsStrIt = joinListIt(objectsIn("it", ::formatItalianSubject, ::formatItalianComplexObject))
    val objectsStrEn = joinListEn(objectsIn("en", ::formatEnglishSubject, ::formatEnglishComplexObject))
    val objectsStrEnWithPrep = formatEnglishObjectPhrase(verbKey, objectsStrEn)
    val objectsStrEs = joinListEs(objectsIn("es", { formatSpanishObject(it, animalIsThing) }, { possessed, owner -> formatSpanishComplexObject(possessed, owner, animalIsThing) }))
    val objectsStrDe = joinListDe(objectsIn("de", ::formatGermanObject, ::formatGermanComplexObject))
    val objectsStrFr = joinListFr(objectsIn("fr", ::formatFrenchSubject, ::formatFrenchComplexObject))

    fun alternativesOf(translations: Map<String, String>): Map<String, List<String>> = translations.mapValues { (languageCode, sentence) ->
        listOfNotNull(
            subjectAdjective?.let { adjectiveAlternatives(sentence, subjects.first(), it, languageCode, asObject = false) },
            objectAdjective?.let { adjectiveAlternatives(sentence, objects.first(), it, languageCode, asObject = true, animalIsThing = animalIsThing) }
        ).flatten()
    }.filterValues { it.isNotEmpty() }
    val hasAdjective = subjectAdjective != null || objectAdjective != null

    if (isPronominal) {
        val chosenPronoun = subjects.first()
        val pronTrans =
            if (chosenPronoun.reading.startsWith(".")) chosenPronoun.reading else ".${chosenPronoun.reading}"
        val rawHieroglyphs = if (isNegative) "D35:N35-${chosenPronoun.gardiner}-D2h2:Z1-${infHiero}-${objectsHiero}" else "M17-G43-${chosenPronoun.gardiner}-D2h2:Z1-${infHiero}-${objectsHiero}"
        val hieroglyphs = if (isNegative) rawHieroglyphs else applyHieroglyphVariations(rawHieroglyphs, random)
        val transliteration = if (isNegative) "nn$pronTrans ḥr ${infTrans} ${objectsTrans}" else "iw$pronTrans ḥr ${infTrans} ${objectsTrans}"

        val translationIt = "${pseudoverbalPronounClause(chosenPronoun.id, verbKey, "it", isNegative, words)} $objectsStrIt"

        val translationEn = "${pseudoverbalPronounClause(chosenPronoun.id, verbKey, "en", isNegative, words)} $objectsStrEnWithPrep"

        val translationEs = "${pseudoverbalPronounClause(chosenPronoun.id, verbKey, "es", isNegative, words)} $objectsStrEs"

        val verbDe = conjugateVerbDe(verbKey, chosenPronoun.id, hasObject = true)
        val pronounDe = when (chosenPronoun.id) {
            "IO" -> "ich"
            "TU_M" -> "du (m.)"
            "TU_F" -> "du (f.)"
            "EGLI" -> "er"
            "ELLA" -> "sie"
            "ESSO" -> "es"
            "NOI" -> "wir"
            "VOI" -> "ihr"
            "ESSI" -> "sie"
            else -> "er"
        }
        val translationDe = moveGermanSeparableParticleToEnd("$pronounDe $verbDe $objectsStrDe${if (isNegative) " nicht" else ""}")

        val translationFr = "${pseudoverbalPronounClause(chosenPronoun.id, verbKey, "fr", isNegative, words)} $objectsStrFr"

        return PseudoverbalObjectSentence(
            hieroglyphs = hieroglyphs,
            transliteration = transliteration,
            translationIt = translationIt,
            translationEn = translationEn,
            translationDe = translationDe,
            translationEs = translationEs,
            translationFr = translationFr,
            isPronominal = true,
            subjectKeys = listOf(chosenPronoun.id),
            verbKey = verbKey,
            objectKeys = objects.map { it.id },
            possessorKey = possessor?.id,
            isDirectGenitive = genitive?.first ?: false,
            isNegative = isNegative,
            alternativeTranslations = alternativesOf(
                mapOf("it" to translationIt, "en" to translationEn, "de" to translationDe, "es" to translationEs, "fr" to translationFr)
            ),
            hasAdjective = hasAdjective
        )
    } else {
        val possessive = subjectPossessive?.takeUnless { thisSubject }
        val subjectsHiero = subjects.mapIndexed { index, word ->
            if (index == 0 && thisSubject) "${word.gardiner}-${getDemonstrativeGardiner(word)}"
            else if (index == 0 && possessive != null) "${word.gardiner}-${possessive.gardiner}"
            else if (index == 0 && subjectAdjective != null) adjectiveHieroglyphs(word, subjectAdjective)
            else word.gardiner
        }.joinToString("-")
        val subjectsTrans = subjects.mapIndexed { index, word ->
            if (index == 0 && thisSubject) "${word.reading} ${getDemonstrativeReading(word)}"
            else if (index == 0 && possessive != null) "${word.reading}.${possessive.reading}"
            else if (index == 0 && subjectAdjective != null) adjectiveTransliteration(word, subjectAdjective)
            else word.reading
        }.joinToString(" ")

        val hieroglyphs = if (isNegative) "D35:N35-${subjectsHiero}-D2h2:Z1-${infHiero}-${objectsHiero}" else "M17-G43-${subjectsHiero}-D2h2:Z1-${infHiero}-${objectsHiero}"
        val transliteration = if (isNegative) "nn ${subjectsTrans} ḥr ${infTrans} ${objectsTrans}" else "iw ${subjectsTrans} ḥr ${infTrans} ${objectsTrans}"

        val isSubjectPlural = subjects.size > 1 || subjects.any { it.isPlural }
        val subjectsStrIt = joinListIt(subjects.mapIndexed { index, word ->
            if (index == 0 && thisSubject) formatItalianDemonstrativeSubject(word)
            else if (index == 0 && possessive != null) possessiveTranslation(word, possessive.id, "it")
            else if (index == 0 && subjectAdjective != null) adjectivePhraseTranslation(word, subjectAdjective, "it").first()
            else formatItalianSubject(word)
        })
        val verbStrIt = pseudoverbalVerbPhrase(verbKey, if (isSubjectPlural) "ESSI" else "EGLI", "it", isNegative, words)
        val translationIt = "$subjectsStrIt $verbStrIt $objectsStrIt"

        val subjectsStrEn = joinListEn(subjects.mapIndexed { index, word ->
            if (index == 0 && thisSubject) formatEnglishDemonstrativeSubject(word)
            else if (index == 0 && possessive != null) possessiveTranslation(word, possessive.id, "en")
            else if (index == 0 && subjectAdjective != null) adjectivePhraseTranslation(word, subjectAdjective, "en").first()
            else formatEnglishSubject(word)
        })
        val verbStrEn = pseudoverbalVerbPhrase(verbKey, if (isSubjectPlural) "ESSI" else "EGLI", "en", isNegative, words)
        val translationEn = "$subjectsStrEn $verbStrEn $objectsStrEnWithPrep"

        val subjectsStrEs = joinListEs(subjects.mapIndexed { index, word ->
            if (index == 0 && thisSubject) formatSpanishDemonstrativeSubject(word)
            else if (index == 0 && possessive != null) possessiveTranslation(word, possessive.id, "es")
            else if (index == 0 && subjectAdjective != null) adjectivePhraseTranslation(word, subjectAdjective, "es").first()
            else formatSpanishSubject(word)
        })
        val verbStrEs = pseudoverbalVerbPhrase(verbKey, if (isSubjectPlural) "ESSI" else "EGLI", "es", isNegative, words)
        val translationEs = "$subjectsStrEs $verbStrEs $objectsStrEs"

        val subjectsStrDe = joinListDe(subjects.mapIndexed { index, word ->
            if (index == 0 && thisSubject) formatGermanDemonstrativeSubject(word)
            else if (index == 0 && possessive != null) possessiveTranslation(word, possessive.id, "de")
            else if (index == 0 && subjectAdjective != null) adjectivePhraseTranslation(word, subjectAdjective, "de").first()
            else formatGermanSubject(word)
        })
        val pronounCode = if (isSubjectPlural) "ESSI" else "EGLI"
        val verbDe = conjugateVerbDe(verbKey, pronounCode, hasObject = true)
        val translationDe = moveGermanSeparableParticleToEnd("$subjectsStrDe $verbDe $objectsStrDe${if (isNegative) " nicht" else ""}")

        val subjectsStrFr = joinListFr(subjects.mapIndexed { index, word ->
            if (index == 0 && thisSubject) formatFrenchDemonstrativeSubject(word)
            else if (index == 0 && possessive != null) possessiveTranslation(word, possessive.id, "fr")
            else if (index == 0 && subjectAdjective != null) adjectivePhraseTranslation(word, subjectAdjective, "fr").first()
            else formatFrenchSubject(word)
        })
        val verbStrFr = pseudoverbalVerbPhrase(verbKey, if (isSubjectPlural) "ESSI" else "EGLI", "fr", isNegative, words)
        val translationFr = "$subjectsStrFr $verbStrFr $objectsStrFr"

        return PseudoverbalObjectSentence(
            hieroglyphs = hieroglyphs,
            transliteration = transliteration,
            translationIt = translationIt,
            translationEn = translationEn,
            translationDe = translationDe,
            translationEs = translationEs,
            translationFr = translationFr,
            isPronominal = false,
            subjectKeys = subjects.map { it.id },
            verbKey = verbKey,
            objectKeys = objects.map { it.id },
            possessorKey = possessor?.id,
            isDirectGenitive = genitive?.first ?: false,
            isNegative = isNegative,
            thisSubject = thisSubject,
            alternativeTranslations = alternativesOf(
                mapOf("it" to translationIt, "en" to translationEn, "de" to translationDe, "es" to translationEs, "fr" to translationFr)
            ),
            hasAdjective = hasAdjective
        )
    }
}

internal fun buildPseudoverbalObjectSentence(
    verbs: List<Verb>,
    words: List<Word>,
    isPronominal: Boolean? = null,
    soggettiCount: Int = 1,
    oggettiCount: Int = 1,
    random: Random = Random,
    verbSentences: List<SentenceOptions> = DatabaseRepositoryImpl.verbSentences,
    isNegative: Boolean = false,
    thisSubject: Boolean = false,
    // The same flags as buildImperfectiveSentenceSync: a suffix possessive on the nominal subject
    // (iw sꜣ.i ḥr wnm t), on the object (iw sš ḥr wnm t.f) or on the object's owner from the row's
    // possessore dell'oggetto (iw sn ḥr wnm dqrw n sš.ṯn). Only nouns that can take one are drawn;
    // if no row has one, that noun just goes without.
    subjectHavePossessive: Boolean = false,
    possessivePairs: List<PossessivePairs> = DatabaseRepositoryImpl.possessivePairs,
    objectHavePossessive: Boolean = false,
    objectPossessorHavePossessive: Boolean = false,
    // An adjective on the nominal subject or the first object, whichever has no possessive.
    haveAdjective: Boolean = false,
    adjectivePairs: List<AdjectivePairs> = DatabaseRepositoryImpl.adjectivePairs
): PseudoverbalObjectSentence {

    fun candidatesOf(rows: List<SentenceOptions>) = rows.filter {
        it.subjects.isNotEmpty() && it.objects.isNotEmpty() && verbs.hasVerbKey(it.verbs.first())
    }

    var effectiveSentences = verbSentences.ifEmpty { DatabaseRepositoryImpl.verbSentences }
    var withSubjectPossessive = false
    if (subjectHavePossessive && !thisSubject) {
        val owned = withPossessableSubjects(effectiveSentences, words, possessivePairs)
        if (candidatesOf(owned).isNotEmpty()) {
            effectiveSentences = owned
            withSubjectPossessive = true
        }
    }
    // The owner takes the place of objectHavePossessive.
    var withObjectOwner = false
    if (objectPossessorHavePossessive) {
        val owned = withPossessablePossessors(effectiveSentences, words, possessivePairs)
        if (candidatesOf(owned).isNotEmpty()) {
            effectiveSentences = owned
            withObjectOwner = true
        }
    }
    var withObjectPossessive = false
    if (objectHavePossessive && !withObjectOwner) {
        val owned = withPossessableObjects(effectiveSentences, words, possessivePairs)
        if (candidatesOf(owned).isNotEmpty()) {
            effectiveSentences = owned
            withObjectPossessive = true
        }
    }
    val withPossessive = withSubjectPossessive || withObjectOwner || withObjectPossessive

    val candidates = candidatesOf(effectiveSentences)
    val pattern =
        if (candidates.isNotEmpty()) candidates.random(random) else effectiveSentences.firstOrNull { verbs.hasVerbKey(it.verbs.first()) }
            ?: effectiveSentences.first()
    val patternSubjects = pattern.subjects
    val verbKey = pattern.verbs.first()
    val patternObjects = pattern.objects

    val chosenVerb = verbs.firstOrNull { it.chiaveWords == verbKey } ?: verbs.first()
    val allowsHumans = patternSubjects.any { isHumanSubject(it, words) }
    val pronominal = if (thisSubject || withSubjectPossessive) false else (isPronominal ?: (allowsHumans && random.nextBoolean()))

    var chosenObjectsKeys = pickDistinctIdsByBaseWord(patternObjects.shuffled(random), oggettiCount, words)
    if (chosenObjectsKeys.isEmpty()) {
        chosenObjectsKeys = listOf(patternObjects.first())
    }
    val chosenObjects = chosenObjectsKeys.map { id ->
        words.firstOrNull { it.id == id } ?: words.first()
    }

    // With a possessive, no word twice ("tua figlia sta amando la figlia", "il pastore sta
    // cacciando la vipera del suo pastore").
    val usedBaseIds = if (withPossessive) chosenObjects.map { it.baseWordId ?: it.id }.toMutableSet() else mutableSetOf()
    val chosenSubjects = if (pronominal) {
        listOf(pickPronounOrFallback(words, random))
    } else {
        var chosenSubjectsKeys = pickDistinctIdsByBaseWord(patternSubjects.shuffled(random), soggettiCount, words, usedBaseIds)
        if (chosenSubjectsKeys.isEmpty()) {
            chosenSubjectsKeys = listOf(patternSubjects.first())
        }
        chosenSubjectsKeys.map { id ->
            words.firstOrNull { it.id == id } ?: words.first()
        }
    }
    // Drawn only when asked for, so the random stream of every other caller stays the same.
    val subjectPossessive = if (withSubjectPossessive) {
        pickPossessivePronoun(chosenSubjects.first(), words, possessivePairs, random)
    } else {
        null
    }
    val objectPossessive = if (withObjectPossessive) {
        pickPossessivePronoun(chosenObjects.first(), words, possessivePairs, random)
    } else {
        null
    }
    val objectOwner = if (withObjectOwner) {
        words.filter { it.id in pattern.possessors && (it.baseWordId ?: it.id) !in usedBaseIds }.randomOrNull(random)?.let { owner ->
            pickPossessivePronoun(owner, words, possessivePairs, random)?.let { pronoun -> owner to pronoun }
        }
    } else {
        null
    }
    val effectiveThisSubject = thisSubject && !pronominal
    val (subjectAdjective, objectAdjective) = if (haveAdjective) {
        pickSentenceAdjective(
            chosenSubjects.first().takeIf { !pronominal && !effectiveThisSubject && subjectPossessive == null },
            chosenObjects.first().takeIf { objectPossessive == null && objectOwner == null },
            words,
            adjectivePairs,
            random
        )
    } else {
        null to null
    }

    return buildExplicitPseudoverbalObjectSentence(
        verb = chosenVerb,
        subjects = chosenSubjects,
        objects = chosenObjects,
        isPronominal = pronominal,
        possessor = objectOwner?.first,
        random = random,
        isNegative = isNegative,
        thisSubject = effectiveThisSubject,
        words = words,
        subjectPossessive = subjectPossessive,
        objectPossessive = objectPossessive,
        possessorPossessive = objectOwner?.second,
        subjectAdjective = subjectAdjective,
        objectAdjective = objectAdjective
    )
}

fun getFrasiComplOggettoQuizContent(
    verbs: List<Verb>,
    words: List<Word>,
    languageCode: String,
    verbSentences: List<SentenceOptions> = DatabaseRepositoryImpl.verbSentences
): List<IQuizContent> {
    val quizzes = mutableListOf<IQuizContent>()

    // 1) 4 SingleSelection questions: Hieroglyphs -> Translation (2 pronominal + 2 nominal)
    val promptSentences = listOf(
        buildPseudoverbalObjectSentence(verbs, words, isPronominal = true, verbSentences = verbSentences),
        buildPseudoverbalObjectSentence(verbs, words, isPronominal = true, verbSentences = verbSentences),
        buildPseudoverbalObjectSentence(verbs, words, isPronominal = false, verbSentences = verbSentences),
        buildPseudoverbalObjectSentence(verbs, words, isPronominal = false, verbSentences = verbSentences)
    )

    for (target in promptSentences) {
        val correctTranslation = target.getTranslation(languageCode)
        val distractors = mutableListOf<String>()
        var attempts = 0
        while (distractors.size < 3 && attempts < 30) {
            attempts++
            val dist =
                buildPseudoverbalObjectSentence(verbs, words, isPronominal = target.isPronominal, verbSentences = verbSentences)
            val distTranslation = dist.getTranslation(languageCode)
            if (distTranslation != correctTranslation && !distractors.contains(distTranslation)) {
                distractors.add(distTranslation)
            }
        }
        val options = (listOf(Option(text = correctTranslation, correct = true)) +
                distractors.map { Option(text = it, correct = false) }).shuffled()

        quizzes.add(
            SingleSelectionContent(
                text = Res.string.quiz_ripasso_cosa_significa_gero,
                detail = target.hieroglyphs,
                options = options,
                info = QuizText.Resource(
                    Res.string.quiz_ripasso_traslitterazione,
                    listOf(target.transliteration)
                )
            )
        )
    }

    // 2) 3 SingleSelection questions: Translation -> Hieroglyphs (with grammatical distractors)
    val transSentences = listOf(
        buildPseudoverbalObjectSentence(verbs, words, isPronominal = true, verbSentences = verbSentences),
        buildPseudoverbalObjectSentence(verbs, words, isPronominal = false, verbSentences = verbSentences),
        buildPseudoverbalObjectSentence(verbs, words, isPronominal = true, verbSentences = verbSentences)
    )

    for (target in transSentences) {
        val targetVerb = verbs.first { it.chiaveWords == target.verbKey }
        val targetObjectsHiero =
            target.objectKeys.mapNotNull { id -> words.find { it.id == id }?.gardiner }
                .joinToString("-")

        // Distractor 1: missing preposition hr (ungrammatical)
        val dist1Hiero = if (target.isPronominal) {
            val pronounWord = words.first { it.id == target.subjectKeys.first() }
            val base =
                "M17-G43-${pronounWord.gardiner}-${targetVerb.formaInfinita.ifBlank { targetVerb.formaBaseSenzaDet }}-${targetObjectsHiero}"
            applyHieroglyphVariations(base)
        } else {
            val subjectsHiero =
                target.subjectKeys.mapNotNull { id -> words.find { it.id == id }?.gardiner }
                    .joinToString("-")
            "M17-G43-${subjectsHiero}-${targetVerb.formaInfinita.ifBlank { targetVerb.formaBaseSenzaDet }}-${targetObjectsHiero}"
        }

        // Distractor 2: different verb/action
        var dist2 =
            buildPseudoverbalObjectSentence(verbs, words, isPronominal = target.isPronominal, verbSentences = verbSentences)
        var dist2Attempts = 0
        while ((dist2.hieroglyphs == target.hieroglyphs || dist2.hieroglyphs == dist1Hiero) && dist2Attempts < 30) {
            dist2 = buildPseudoverbalObjectSentence(verbs, words, isPronominal = target.isPronominal, verbSentences = verbSentences)
            dist2Attempts++
        }

        // Distractor 3: different sentence
        var dist3 =
            buildPseudoverbalObjectSentence(verbs, words, isPronominal = !target.isPronominal, verbSentences = verbSentences)
        var dist3Attempts = 0
        while ((dist3.hieroglyphs == target.hieroglyphs || dist3.hieroglyphs == dist1Hiero || dist3.hieroglyphs == dist2.hieroglyphs) && dist3Attempts < 30) {
            dist3 = buildPseudoverbalObjectSentence(verbs, words, isPronominal = !target.isPronominal, verbSentences = verbSentences)
            dist3Attempts++
        }

        val options = listOf(
            Option(text = target.hieroglyphs, correct = true),
            Option(text = dist1Hiero, correct = false),
            Option(text = dist2.hieroglyphs, correct = false),
            Option(text = dist3.hieroglyphs, correct = false)
        ).shuffled()

        quizzes.add(
            SingleSelectionContent(
                text = Res.string.quiz_ripasso_quale_gero_significa,
                textArgs = listOf(target.getTranslation(languageCode)),
                detail = "",
                options = options
            )
        )
    }

    // 3) 3 PhoneticKeyboard questions: transliterate the sentence
    val keyboardSentences = listOf(
        buildPseudoverbalObjectSentence(verbs, words, isPronominal = true, verbSentences = verbSentences),
        buildPseudoverbalObjectSentence(verbs, words, isPronominal = false, verbSentences = verbSentences),
        buildPseudoverbalObjectSentence(verbs, words, isPronominal = true, verbSentences = verbSentences)
    )

    for (target in keyboardSentences) {
        quizzes.add(
            PhoneticKeyboardContent(
                sentence = target.hieroglyphs,
                info = "${target.transliteration} -> ${target.getTranslation(languageCode)}",
                reading = target.transliteration.map { it.toString() },
                isProperName = false,
                canRotate = false
            )
        )
    }

    return quizzes.shuffled()
}

private fun buildQuizListForMode(
    verbs: List<Verb>,
    words: List<Word>,
    languageCode: String,
    pronominalOnly: Boolean,
    verbSentences: List<SentenceOptions> = DatabaseRepositoryImpl.verbSentences
): List<IQuizContent> {
    val quizzes = mutableListOf<IQuizContent>()

    // 1) 4 SingleSelection questions: Hieroglyphs -> Translation
    val promptSentences = List(4) {
        getRandomPseudoverbalSentence(verbs, words, isPronominal = pronominalOnly, verbSentences = verbSentences)
    }

    for (target in promptSentences) {
        val correctTranslation = target.getTranslation(languageCode)
        val distractors = mutableListOf<String>()
        var attempts = 0
        while (distractors.size < 3 && attempts < 25) {
            attempts++
            val dist = getRandomPseudoverbalSentence(verbs, words, isPronominal = pronominalOnly, verbSentences = verbSentences)
            val distTranslation = dist.getTranslation(languageCode)
            if (distTranslation != correctTranslation && !distractors.contains(distTranslation)) {
                distractors.add(distTranslation)
            }
        }
        val options = (listOf(Option(text = correctTranslation, correct = true)) +
                distractors.map { Option(text = it, correct = false) }).shuffled()

        quizzes.add(
            SingleSelectionContent(
                text = Res.string.quiz_ripasso_cosa_significa_gero,
                detail = target.hieroglyphs,
                options = options,
                info = QuizText.Resource(
                    Res.string.quiz_ripasso_traslitterazione,
                    listOf(target.transliteration)
                )
            )
        )
    }

    // 2) 3 SingleSelection questions: Translation -> Hieroglyphs (with grammatical distractors)
    val transSentences = List(3) {
        getRandomPseudoverbalSentence(verbs, words, isPronominal = pronominalOnly, verbSentences = verbSentences)
    }

    for (target in transSentences) {
        val targetVerb = verbs.first { it.chiaveWords == target.verbKey }
        val targetSubject = words.first { it.id == target.subjectKey }

        // Distractor 1: missing preposition hr (ungrammatical)
        val dist1Hiero = if (target.isPronominal) {
            val base =
                "M17-G43-${targetSubject.gardiner}-${targetVerb.formaInfinita.ifBlank { targetVerb.formaBaseSenzaDet }}"
            applyHieroglyphVariations(base)
        } else {
            "M17-G43-${targetSubject.gardiner}-${targetVerb.formaInfinita.ifBlank { targetVerb.formaBaseSenzaDet }}"
        }

        // Distractor 2: different coherent sentence
        var dist2 = getRandomPseudoverbalSentence(verbs, words, isPronominal = pronominalOnly, verbSentences = verbSentences)
        var attemptsDist = 0
        while (dist2.hieroglyphs == target.hieroglyphs && attemptsDist < 10) {
            dist2 = getRandomPseudoverbalSentence(verbs, words, isPronominal = pronominalOnly, verbSentences = verbSentences)
            attemptsDist++
        }

        // Distractor 3: different coherent sentence
        var dist3 = getRandomPseudoverbalSentence(verbs, words, isPronominal = pronominalOnly, verbSentences = verbSentences)
        attemptsDist = 0
        while ((dist3.hieroglyphs == target.hieroglyphs || dist3.hieroglyphs == dist2.hieroglyphs) && attemptsDist < 10) {
            dist3 = getRandomPseudoverbalSentence(verbs, words, isPronominal = pronominalOnly, verbSentences = verbSentences)
            attemptsDist++
        }

        val options = listOf(
            Option(text = target.hieroglyphs, correct = true),
            Option(text = dist1Hiero, correct = false),
            Option(text = dist2.hieroglyphs, correct = false),
            Option(text = dist3.hieroglyphs, correct = false)
        ).shuffled()

        quizzes.add(
            SingleSelectionContent(
                text = Res.string.quiz_ripasso_quale_gero_significa,
                textArgs = listOf(target.getTranslation(languageCode)),
                detail = "",
                options = options
            )
        )
    }

    // 3) 3 PhoneticKeyboard questions: transliterate the sentence
    val keyboardSentences = List(3) {
        getRandomPseudoverbalSentence(verbs, words, isPronominal = pronominalOnly, verbSentences = verbSentences)
    }

    for (target in keyboardSentences) {
        quizzes.add(
            PhoneticKeyboardContent(
                sentence = target.hieroglyphs,
                info = "${target.transliteration} -> ${target.getTranslation(languageCode)}",
                reading = target.transliteration.map { it.toString() },
                isProperName = false,
                canRotate = false
            )
        )
    }

    return quizzes.shuffled()
}

fun getFrasiConPronomiQuizContent(
    verbs: List<Verb>,
    words: List<Word>,
    languageCode: String,
    verbSentences: List<SentenceOptions> = DatabaseRepositoryImpl.verbSentences
): List<IQuizContent> = buildQuizListForMode(
    verbs,
    words,
    languageCode,
    pronominalOnly = true,
    verbSentences = verbSentences
)

fun getFrasiConSoggettoQuizContent(
    verbs: List<Verb>,
    words: List<Word>,
    languageCode: String,
    verbSentences: List<SentenceOptions> = DatabaseRepositoryImpl.verbSentences
): List<IQuizContent> = buildQuizListForMode(
    verbs,
    words,
    languageCode,
    pronominalOnly = false,
    verbSentences = verbSentences
)

internal fun getFrasiPseudoverbaliQuizContent(
    verbs: List<Verb>,
    words: List<Word>,
    languageCode: String
): List<IQuizContent> = getFrasiConPronomiQuizContent(verbs, words, languageCode)

internal fun buildExplicitPseudoverbalComplexObjectSentence(
    verb: Verb,
    subject: Word,
    isPronominal: Boolean,
    possessed: Word,
    possessor: Word,
    isDirect: Boolean,
    connector: String = "-",
    particleTransliteration: String = "",
    random: Random = Random,
    isNegative: Boolean = false,
    thisSubject: Boolean = false,
    words: List<Word> = DatabaseRepositoryImpl.words
): PseudoverbalComplexObjectSentence {
    val verbKey = verb.chiaveWords
    val infHiero = verb.formaInfinita.ifBlank { verb.formaBaseSenzaDet }
    val infTrans = verb.traslitterazioneInfinita.ifBlank { verb.traslitterazione }

    val objectHiero = if (isDirect) {
        "${possessed.gardiner}-${possessor.gardiner}"
    } else {
        "${possessed.gardiner}${connector}${possessor.gardiner}"
    }

    val objectTrans = if (isDirect || particleTransliteration.isBlank()) {
        "${possessed.reading} ${possessor.reading}"
    } else {
        "${possessed.reading} $particleTransliteration ${possessor.reading}"
    }

    val objectStrIt = formatItalianComplexObject(possessed, possessor)
    val objectStrEn = formatEnglishComplexObject(possessed, possessor)
    val objectStrEnWithPrep = formatEnglishObjectPhrase(verbKey, objectStrEn)
    val objectStrEs = formatSpanishComplexObject(possessed, possessor, animalObjectIsThing(verbKey, words))
    val objectStrDe = formatGermanComplexObject(possessed, possessor)
    val objectStrFr = formatFrenchComplexObject(possessed, possessor)

    if (isPronominal) {
        val pronTrans =
            if (subject.reading.startsWith(".")) subject.reading else ".${subject.reading}"
        val rawHieroglyphs = if (isNegative) "D35:N35-${subject.gardiner}-D2h2:Z1-${infHiero}-${objectHiero}" else "M17-G43-${subject.gardiner}-D2h2:Z1-${infHiero}-${objectHiero}"
        val hieroglyphs = if (isNegative) rawHieroglyphs else applyHieroglyphVariations(rawHieroglyphs, random)
        val transliteration = if (isNegative) "nn$pronTrans ḥr ${infTrans} ${objectTrans}" else "iw$pronTrans ḥr ${infTrans} ${objectTrans}"

        val translationIt = "${pseudoverbalPronounClause(subject.id, verbKey, "it", isNegative, words)} $objectStrIt"

        val translationEn = "${pseudoverbalPronounClause(subject.id, verbKey, "en", isNegative, words)} $objectStrEnWithPrep"

        val translationEs = "${pseudoverbalPronounClause(subject.id, verbKey, "es", isNegative, words)} $objectStrEs"

        val verbDe = conjugateVerbDe(verbKey, subject.id, hasObject = true)
        val pronounDe = when (subject.id) {
            "IO" -> "ich"
            "TU_M" -> "du (m.)"
            "TU_F" -> "du (f.)"
            "EGLI" -> "er"
            "ELLA" -> "sie"
            "ESSO" -> "es"
            "NOI" -> "wir"
            "VOI" -> "ihr"
            "ESSI" -> "sie"
            else -> "er"
        }
        val translationDe = moveGermanSeparableParticleToEnd("$pronounDe $verbDe $objectStrDe${if (isNegative) " nicht" else ""}")

        val translationFr = "${pseudoverbalPronounClause(subject.id, verbKey, "fr", isNegative, words)} $objectStrFr"

        return PseudoverbalComplexObjectSentence(
            hieroglyphs = hieroglyphs,
            transliteration = transliteration,
            translationIt = translationIt,
            translationEn = translationEn,
            translationDe = translationDe,
            translationEs = translationEs,
            translationFr = translationFr,
            isPronominal = true,
            isDirectGenitive = isDirect,
            subjectKey = subject.id,
            verbKey = verbKey,
            possessedKey = possessed.id,
            possessorKey = possessor.id,
            isNegative = isNegative
        )
    } else {
        val subjHiero = if (thisSubject) "${subject.gardiner}-${getDemonstrativeGardiner(subject)}" else subject.gardiner
        val subjTrans = if (thisSubject) "${subject.reading} ${getDemonstrativeReading(subject)}" else subject.reading

        val hieroglyphs = if (isNegative) "D35:N35-${subjHiero}-D2h2:Z1-${infHiero}-${objectHiero}" else "M17-G43-${subjHiero}-D2h2:Z1-${infHiero}-${objectHiero}"
        val transliteration = if (isNegative) "nn ${subjTrans} ḥr ${infTrans} ${objectTrans}" else "iw ${subjTrans} ḥr ${infTrans} ${objectTrans}"

        val subjStrIt = if (thisSubject) formatItalianDemonstrativeSubject(subject) else formatItalianSubject(subject)
        val verbStrIt = pseudoverbalVerbPhrase(verbKey, if (subject.isPlural) "ESSI" else "EGLI", "it", isNegative, words)
        val translationIt = "$subjStrIt $verbStrIt $objectStrIt"

        val subjStrEn = if (thisSubject) formatEnglishDemonstrativeSubject(subject) else formatEnglishSubject(subject)
        val verbStrEn = pseudoverbalVerbPhrase(verbKey, if (subject.isPlural) "ESSI" else "EGLI", "en", isNegative, words)
        val translationEn = "$subjStrEn $verbStrEn $objectStrEnWithPrep"

        val subjStrEs = if (thisSubject) formatSpanishDemonstrativeSubject(subject) else formatSpanishSubject(subject)
        val verbStrEs = pseudoverbalVerbPhrase(verbKey, if (subject.isPlural) "ESSI" else "EGLI", "es", isNegative, words)
        val translationEs = "$subjStrEs $verbStrEs $objectStrEs"

        val subjStrDe = if (thisSubject) formatGermanDemonstrativeSubject(subject) else formatGermanSubject(subject)
        val pronounCode = if (subject.isPlural) "ESSI" else "EGLI"
        val verbDe = conjugateVerbDe(verbKey, pronounCode, hasObject = true)
        val translationDe = moveGermanSeparableParticleToEnd("$subjStrDe $verbDe $objectStrDe${if (isNegative) " nicht" else ""}")

        val subjStrFr = if (thisSubject) formatFrenchDemonstrativeSubject(subject) else formatFrenchSubject(subject)
        val verbStrFr = pseudoverbalVerbPhrase(verbKey, if (subject.isPlural) "ESSI" else "EGLI", "fr", isNegative, words)
        val translationFr = "$subjStrFr $verbStrFr $objectStrFr"

        return PseudoverbalComplexObjectSentence(
            hieroglyphs = hieroglyphs,
            transliteration = transliteration,
            translationIt = translationIt,
            translationEn = translationEn,
            translationDe = translationDe,
            translationEs = translationEs,
            translationFr = translationFr,
            isPronominal = false,
            isDirectGenitive = isDirect,
            subjectKey = subject.id,
            verbKey = verbKey,
            possessedKey = possessed.id,
            possessorKey = possessor.id,
            isNegative = isNegative,
            thisSubject = thisSubject
        )
    }
}


internal fun determineGenitiveDetails(
    possessed: Word,
    possessor: Word,
    preferDirect: Boolean? = null,
    random: Random = Random
): Pair<Boolean, Pair<String, String>> {
    val directEligible = possessor.id in setOf(
        "PADRE",
        "MADRE",
        "SCRIBA",
        "SACERDOTE",
        "RE",
        "DIO",
        "GATTO",
        "PASTORE"
    )
    val isDirect = if (preferDirect != null) {
        preferDirect
    } else if (directEligible) {
        random.nextBoolean()
    } else {
        false
    }

    val (connector, particleTranslit) = if (isDirect) {
        Pair("-", "")
    } else {
        val isFem = possessed.isFeminine
        if (possessed.isPlural) {
            if (isFem) Pair("-N35:X1-", "nt") else Pair("-N35:Z7-", "nw")
        } else {
            if (isFem) Pair("-N35:X1-", "nt") else Pair(":N35-", "n")
        }
    }
    return Pair(isDirect, Pair(connector, particleTranslit))
}

internal fun buildPseudoverbalComplexObjectSentence(
    verbs: List<Verb>,
    words: List<Word>,
    isPronominal: Boolean? = null,
    isDirect: Boolean? = null,
    random: Random = Random,
    verbSentences: List<SentenceOptions> = DatabaseRepositoryImpl.verbSentences,
    isNegative: Boolean = false,
    thisSubject: Boolean = false
): PseudoverbalComplexObjectSentence {
    val wordMap = words.associateBy { it.id.uppercase() }

    val parsedPatterns = verbSentences

    val validPatterns = parsedPatterns.filter { pat ->
        pat.verbs.isNotEmpty() &&
                verbs.hasVerbKey(pat.verbs.first()) &&
                pat.objects.anyResolvable(wordMap) &&
                pat.subjects.anyResolvable(wordMap) &&
                pat.possessors.anyResolvable(wordMap)
    }

    if (validPatterns.isEmpty()) {
        val fallback = buildPseudoverbalObjectSentence(
            verbs = verbs,
            words = words,
            isPronominal = isPronominal,
            random = random,
            verbSentences = verbSentences,
            isNegative = isNegative,
            thisSubject = thisSubject
        )
        return PseudoverbalComplexObjectSentence(
            hieroglyphs = fallback.hieroglyphs,
            transliteration = fallback.transliteration,
            translationIt = fallback.translationIt,
            translationEn = fallback.translationEn,
            translationDe = fallback.translationDe,
            translationEs = fallback.translationEs,
            translationFr = fallback.translationFr,
            isPronominal = fallback.isPronominal,
            isDirectGenitive = fallback.isDirectGenitive,
            subjectKey = fallback.subjectKeys.firstOrNull() ?: "",
            verbKey = fallback.verbKey,
            possessedKey = fallback.objectKeys.firstOrNull() ?: "",
            possessorKey = fallback.possessorKey ?: "",
            isNegative = fallback.isNegative,
            thisSubject = fallback.thisSubject
        )
    }

    val pattern = validPatterns.random(random)
    val verb = verbs.first { it.chiaveWords == pattern.verbs.first() }
    val availableObjects = pattern.objects.mapNotNull { wordMap[it] }
    val availablePossessors = pattern.possessors.mapNotNull { wordMap[it] }

    val possessed = availableObjects.random(random)
    val possessor = availablePossessors.random(random)

    val pronominal = if (thisSubject) false else (isPronominal ?: random.nextBoolean())
    val subject = if (pronominal) {
        pickPronounOrFallback(words, random)
    } else {
        val availableSubjects =
            pattern.subjects.mapNotNull { wordMap[it] }.filter { it.id != possessor.id }
        if (availableSubjects.isNotEmpty()) availableSubjects.random(random)
        else words.first { it.isCommonLivingNoun() }
    }

    val (direct, connectorPair) = determineGenitiveDetails(
        possessed,
        possessor,
        isDirect,
        random
    )

    return buildExplicitPseudoverbalComplexObjectSentence(
        verb = verb,
        subject = subject,
        isPronominal = pronominal,
        possessed = possessed,
        possessor = possessor,
        isDirect = direct,
        connector = connectorPair.first,
        particleTransliteration = connectorPair.second,
        random = random,
        isNegative = isNegative,
        thisSubject = thisSubject,
        words = words
    )
}

data class RichestPseudoverbalResult(
    val hieroglyphs: String,
    val transliteration: String,
    val translation: String
)

// Picks a random row from `pool`, then the richest construction THAT row supports -
// object + possessor (buildPseudoverbalComplexObjectSentence), just an object
// (buildPseudoverbalObjectSentence), or bare subject + verb
// (getRandomPseudoverbalSentence) - instead of committing to one construction for
// every pick regardless of what's actually available. Originally a local helper in
// chapter12/torniamo_alla_grammatica.kt; promoted here so any lesson that needs
// "the richest sentence a row supports" (not just chapter 12's recap) can reuse it.
internal fun buildRichestPseudoverbalSentence(
    pool: List<SentenceOptions>,
    verbs: List<Verb>,
    words: List<Word>,
    isPronominal: Boolean,
    languageCode: String,
    random: Random,
    isNegative: Boolean = false,
    thisSubject: Boolean = false
): RichestPseudoverbalResult {
    val row = pool.random(random)
    val singleRow = listOf(row)
    val pronominal = if (thisSubject) false else isPronominal
    return when {
        row.objects.isNotEmpty() && row.possessors.isNotEmpty() -> {
            val s = buildPseudoverbalComplexObjectSentence(
                verbs, words, isPronominal = pronominal, random = random, verbSentences = singleRow, isNegative = isNegative, thisSubject = thisSubject
            )
            RichestPseudoverbalResult(s.hieroglyphs, s.transliteration, s.getTranslation(languageCode))
        }

        row.objects.isNotEmpty() -> {
            val s = buildPseudoverbalObjectSentence(
                verbs, words, isPronominal = pronominal, random = random, verbSentences = singleRow, isNegative = isNegative, thisSubject = thisSubject
            )
            RichestPseudoverbalResult(s.hieroglyphs, s.transliteration, s.getTranslation(languageCode))
        }

        else -> {
            val s = getRandomPseudoverbalSentence(
                verbs, words, isPronominal = pronominal, random = random, verbSentences = singleRow, isNegative = isNegative, thisSubject = thisSubject
            )
            RichestPseudoverbalResult(s.hieroglyphs, s.transliteration, s.getTranslation(languageCode))
        }
    }
}

fun getComplementiComplessiQuizContent(
    verbs: List<Verb>,
    words: List<Word>,
    languageCode: String,
    verbSentences: List<SentenceOptions> = DatabaseRepositoryImpl.verbSentences
): List<IQuizContent> {
    val quizzes = mutableListOf<IQuizContent>()

    // 1) 4 SingleSelection questions: Hieroglyphs -> Translation (2 pronominal + 2 noun, direct + indirect)
    val promptSentences = listOf(
        buildPseudoverbalComplexObjectSentence(verbs, words, isPronominal = true, isDirect = true, verbSentences = verbSentences),
        buildPseudoverbalComplexObjectSentence(verbs, words, isPronominal = true, isDirect = false, verbSentences = verbSentences),
        buildPseudoverbalComplexObjectSentence(verbs, words, isPronominal = false, isDirect = true, verbSentences = verbSentences),
        buildPseudoverbalComplexObjectSentence(verbs, words, isPronominal = false, isDirect = false, verbSentences = verbSentences)
    )

    for (target in promptSentences) {
        val correctTranslation = target.getTranslation(languageCode)
        val distractors = mutableListOf<String>()
        var attempts = 0
        while (distractors.size < 3 && attempts < 35) {
            attempts++
            val dist = buildPseudoverbalComplexObjectSentence(
                verbs,
                words,
                isPronominal = target.isPronominal,
                verbSentences = verbSentences
            )
            val distTranslation = dist.getTranslation(languageCode)
            if (distTranslation != correctTranslation && !distractors.contains(distTranslation)) {
                distractors.add(distTranslation)
            }
        }
        val options = (listOf(Option(text = correctTranslation, correct = true)) +
                distractors.map { Option(text = it, correct = false) }).shuffled()

        quizzes.add(
            SingleSelectionContent(
                text = Res.string.quiz_ripasso_cosa_significa_gero,
                detail = target.hieroglyphs,
                options = options,
                info = QuizText.Resource(
                    Res.string.quiz_ripasso_traslitterazione,
                    listOf(target.transliteration)
                )
            )
        )
    }

    // 2) 3 SingleSelection questions: Translation -> Hieroglyphs (with grammatical distractors)
    val transSentences = listOf(
        buildPseudoverbalComplexObjectSentence(verbs, words, isPronominal = true, isDirect = false, verbSentences = verbSentences),
        buildPseudoverbalComplexObjectSentence(verbs, words, isPronominal = false, isDirect = true, verbSentences = verbSentences),
        buildPseudoverbalComplexObjectSentence(verbs, words, isPronominal = true, isDirect = true, verbSentences = verbSentences)
    )

    for (target in transSentences) {
        val targetVerb = verbs.firstOrNull { it.chiaveWords == target.verbKey } ?: verbs.first()
        val targetPossessed = words.firstOrNull { it.id == target.possessedKey }
        val targetPossessor = words.firstOrNull { it.id == target.possessorKey }

        val targetObjectHiero = if (targetPossessed != null && targetPossessor != null) {
            if (target.isDirectGenitive) {
                "${targetPossessed.gardiner}-${targetPossessor.gardiner}"
            } else {
                val connector = determineGenitiveDetails(
                    targetPossessed,
                    targetPossessor,
                    preferDirect = false
                ).second.first
                "${targetPossessed.gardiner}${connector}${targetPossessor.gardiner}"
            }
        } else {
            targetPossessed?.gardiner ?: ""
        }

        // Distractor 1: missing preposition hr (ungrammatical)
        val dist1Hiero = if (target.isPronominal) {
            val pronounWord = words.firstOrNull { it.id == target.subjectKey } ?: pickPronounOrFallback(words, Random)
            val base =
                "M17-G43-${pronounWord.gardiner}-${targetVerb.formaInfinita.ifBlank { targetVerb.formaBaseSenzaDet }}-${targetObjectHiero}"
            applyHieroglyphVariations(base)
        } else {
            val subjectWord = words.firstOrNull { it.id == target.subjectKey } ?: words.firstOrNull { it.isCommonLivingNoun() } ?: words.first()
            "M17-G43-${subjectWord.gardiner}-${targetVerb.formaInfinita.ifBlank { targetVerb.formaBaseSenzaDet }}-${targetObjectHiero}"
        }

        // Distractor 2: different sentence
        var dist2 =
            buildPseudoverbalComplexObjectSentence(verbs, words, isPronominal = target.isPronominal, verbSentences = verbSentences)
        var attemptsDist = 0
        while (dist2.hieroglyphs == target.hieroglyphs && attemptsDist < 10) {
            dist2 = buildPseudoverbalComplexObjectSentence(
                verbs,
                words,
                isPronominal = target.isPronominal,
                verbSentences = verbSentences
            )
            attemptsDist++
        }

        // Distractor 3: different sentence
        var dist3 = buildPseudoverbalComplexObjectSentence(
            verbs,
            words,
            isPronominal = !target.isPronominal,
            verbSentences = verbSentences
        )
        attemptsDist = 0
        while ((dist3.hieroglyphs == target.hieroglyphs || dist3.hieroglyphs == dist2.hieroglyphs) && attemptsDist < 10) {
            dist3 = buildPseudoverbalComplexObjectSentence(
                verbs,
                words,
                isPronominal = !target.isPronominal,
                verbSentences = verbSentences
            )
            attemptsDist++
        }

        val options = listOf(
            Option(text = target.hieroglyphs, correct = true),
            Option(text = dist1Hiero, correct = false),
            Option(text = dist2.hieroglyphs, correct = false),
            Option(text = dist3.hieroglyphs, correct = false)
        ).shuffled()

        quizzes.add(
            SingleSelectionContent(
                text = Res.string.quiz_ripasso_quale_gero_significa,
                textArgs = listOf(target.getTranslation(languageCode)),
                detail = "",
                options = options
            )
        )
    }

    // 3) 3 PhoneticKeyboard questions: transliterate the sentence
    val keyboardSentences = listOf(
        buildPseudoverbalComplexObjectSentence(verbs, words, isPronominal = true, isDirect = true, verbSentences = verbSentences),
        buildPseudoverbalComplexObjectSentence(
            verbs,
            words,
            isPronominal = false,
            isDirect = false,
            verbSentences = verbSentences
        ),
        buildPseudoverbalComplexObjectSentence(verbs, words, isPronominal = true, isDirect = false, verbSentences = verbSentences)
    )

    for (target in keyboardSentences) {
        quizzes.add(
            PhoneticKeyboardContent(
                sentence = target.hieroglyphs,
                info = "${target.transliteration} -> ${target.getTranslation(languageCode)}",
                reading = target.transliteration.map { it.toString() },
                isProperName = false,
                canRotate = false
            )
        )
    }

    return quizzes.shuffled()
}

internal fun buildWordBankFromWords(
    correctPhrase: String,
    words: List<Word>,
    verbs: List<Verb>,
    languageCode: String,
    random: Random = Random,
    distractorCount: Int = 5
): List<String> {
    val correctTokens = tokenizePhrase(correctPhrase)

    // tokenizePhrase splits any multi-word field (a compound vocabulary meaning like
    // "König von Oberägypten", or a multi-word Spanish genitive like "de la") into
    // separate one-word tiles instead of one glued distractor tile.
    val wrongTokens = mutableListOf<String>()
    val shuffledWords = DistractorPool.words(words).filter { it.showInCompendio }.shuffled(random).take(10)
    for (w in shuffledWords) {
        w.getArticolo(languageCode)?.takeIf { it != "-" && it.isNotBlank() }
            ?.let { wrongTokens.addAll(tokenizePhrase(it)) }
        val meaning = w.inlineMeaning(languageCode, w.getMeaning(languageCode).split(",")[0].split("/")[0].trim())
        if (meaning.isNotBlank()) {
            wrongTokens.addAll(tokenizePhrase(meaning))
        }
        w.getGenitivo(languageCode)?.takeIf { it != "-" && it.isNotBlank() }
            ?.let { wrongTokens.addAll(tokenizePhrase(it)) }
    }

    val shuffledVerbs = DistractorPool.verbs(verbs).shuffled(random).take(5)
    for (v in shuffledVerbs) {
        val gerund = when (languageCode) {
            "it" -> getGerundIt(v.chiaveWords)
            "de" -> conjugateVerbDe(v.chiaveWords, "EGLI")
            "es" -> getGerundEs(v.chiaveWords)
            "fr" -> getEnTrainDeFr(v.chiaveWords)
            else -> getGerundEn(v.chiaveWords)
        }
        // Some English gerunds are phrasal (e.g. "going out" for USCIRE) — split them too.
        wrongTokens.addAll(tokenizePhrase(gerund))
    }

    val distractors = wrongTokens.filter { token ->
        val norm = token.trim().lowercase()
        norm.isNotBlank() && correctTokens.none { it.trim().lowercase() == norm }
    }.distinct().shuffled(random).take(distractorCount)

    return (correctTokens + distractors).shuffled(random)
}

// buildPseudoverbalComplexObjectSentence throws when verbSentences has no row
// with a seen subject/object/possessor - a real possibility for the "seen"
// pool this recap is built to respect. Same reasoning as the VSO fallback
// just above: try the seen-filtered pool first, and only fall back to the
// full one if it genuinely can't produce a sentence, so this recap never
// crashes for a player who hasn't unlocked quite enough yet.
private fun buildPseudoverbalComplexObjectSentenceWithFallback(
    verbs: List<Verb>,
    words: List<Word>,
    isPronominal: Boolean? = null,
    isDirect: Boolean? = null,
    random: Random,
    verbSentences: List<SentenceOptions>
): PseudoverbalComplexObjectSentence {
    return try {
        buildPseudoverbalComplexObjectSentence(verbs, words, isPronominal, isDirect, random, verbSentences)
    } catch (_: IllegalStateException) {
        buildPseudoverbalComplexObjectSentence(verbs, words, isPronominal, isDirect, random)
    }
}

fun getRecapFrasiPseudoverbaliQuizContent(
    verbs: List<Verb>,
    words: List<Word>,
    languageCode: String,
    random: Random = Random,
    verbSentences: List<SentenceOptions> = DatabaseRepositoryImpl.verbSentences
): List<IQuizContent> {

    // 1) 2 Frasi VSO: 1 WordBank + 1 TextTranslation (usando i generatori VSO esistenti)
    // Prova prima col pool "visto" (verbSentences), cosi le frasi VSO di questo
    // recap rispettano cosa il giocatore ha davvero sbloccato; se quel pool e
    // troppo scarno per produrne 2 (es. i verbi/pronomi introdotti solo in
    // questo capitolo non bastano ancora), ripiega sul pool completo invece di
    // andare in IndexOutOfBoundsException su vsoQuizzes[1] - meglio una frase
    // sempre valida che un crash.
    var vsoQuizzes = com.shadowings.gero.data.lessons.buildVsoRecapWordSelectionQuizzes(
        random = random,
        words = words,
        verbs = verbs,
        languageCode = languageCode,
        count = 2,
        verbSentences = verbSentences
    )
    if (vsoQuizzes.size < 2) {
        vsoQuizzes = com.shadowings.gero.data.lessons.buildVsoRecapWordSelectionQuizzes(
            random = random,
            words = words,
            verbs = verbs,
            languageCode = languageCode,
            count = 2
        )
    }
    val vsoWordBank = vsoQuizzes[0] as WordSelectionContent
    val vso2Wb = vsoQuizzes[1] as WordSelectionContent
    val vsoTextTrans = TextTranslationContent(
        hieroglyphs = vso2Wb.hieroglyphs,
        correctPhrase = vso2Wb.correctPhrase,
        transliteration = vso2Wb.transliteration,
        id = vso2Wb.id
    )

    // 2) 2 Frasi avverbiali: 1 WordBank + 1 TextTranslation (usando i generatori avverbiali esistenti)
    val adv1 = com.shadowings.gero.data.lessons.buildAdverbialSentenceSync(
        soggettiCount = 1,
        withParticleIw = true,
        random = random,
        words = words,
    )
    val adv1Translation = when (languageCode) {
        "it" -> adv1.translationIt
        "de" -> adv1.translationDe
        "es" -> adv1.translationEs
        "fr" -> adv1.translationFr
        else -> adv1.translationEn
    }
    val advWordBank = WordSelectionContent(
        hieroglyphs = adv1.hieroglyphs,
        correctPhrase = adv1Translation,
        words = buildWordBankFromWords(adv1Translation, words, verbs, languageCode, random),
        transliteration = adv1.transliteration,
        soggettiCount = 1
    )

    val advTextQuizzes = com.shadowings.gero.data.lessons.buildAdverbialTextTranslationQuizzes(
        random = random,
        words = words,
        languageCode = languageCode,
        count = 1,
        withParticleIw = true
    )
    val advTextTrans = advTextQuizzes.first()

    // 3) 6 Frasi pseudoverbali complete: 2 WordBank + 2 TextTranslation + 2 PhoneticKeyboard
    // 3a) 2 WordBank (1 pronominal + 1 nominal)
    val pseudoWb1 =
        buildPseudoverbalComplexObjectSentenceWithFallback(verbs, words, isPronominal = true, random = random, verbSentences = verbSentences)
    val pseudoWb1Trans = pseudoWb1.getTranslation(languageCode)
    val pseudoWordBank1 = WordSelectionContent(
        hieroglyphs = pseudoWb1.hieroglyphs,
        correctPhrase = pseudoWb1Trans,
        words = buildWordBankFromWords(pseudoWb1Trans, words, verbs, languageCode, random),
        transliteration = pseudoWb1.transliteration
    )

    val pseudoWb2 =
        buildPseudoverbalComplexObjectSentenceWithFallback(verbs, words, isPronominal = false, random = random, verbSentences = verbSentences)
    val pseudoWb2Trans = pseudoWb2.getTranslation(languageCode)
    val pseudoWordBank2 = WordSelectionContent(
        hieroglyphs = pseudoWb2.hieroglyphs,
        correctPhrase = pseudoWb2Trans,
        words = buildWordBankFromWords(pseudoWb2Trans, words, verbs, languageCode, random),
        transliteration = pseudoWb2.transliteration
    )

    // 3b) 2 TextTranslation (1 pronominal + 1 nominal)
    val pseudoTt1 =
        buildPseudoverbalComplexObjectSentenceWithFallback(verbs, words, isPronominal = true, random = random, verbSentences = verbSentences)
    val pseudoTt1Trans = pseudoTt1.getTranslation(languageCode)
    val pseudoTextTrans1 = TextTranslationContent(
        hieroglyphs = pseudoTt1.hieroglyphs,
        correctPhrase = pseudoTt1Trans,
        transliteration = pseudoTt1.transliteration
    )

    val pseudoTt2 =
        buildPseudoverbalComplexObjectSentenceWithFallback(verbs, words, isPronominal = false, random = random, verbSentences = verbSentences)
    val pseudoTt2Trans = pseudoTt2.getTranslation(languageCode)
    val pseudoTextTrans2 = TextTranslationContent(
        hieroglyphs = pseudoTt2.hieroglyphs,
        correctPhrase = pseudoTt2Trans,
        transliteration = pseudoTt2.transliteration
    )

    // 3c) 2 PhoneticKeyboard (1 pronominal + 1 nominal)
    val pseudoPk1 =
        buildPseudoverbalComplexObjectSentenceWithFallback(verbs, words, isPronominal = true, random = random, verbSentences = verbSentences)
    val pseudoPk1Trans = pseudoPk1.getTranslation(languageCode)
    val pseudoKeyboard1 = PhoneticKeyboardContent(
        sentence = pseudoPk1.hieroglyphs,
        info = "${pseudoPk1.transliteration} -> ${pseudoPk1Trans}",
        reading = pseudoPk1.transliteration.map { it.toString() },
        isProperName = false,
        canRotate = false
    )

    val pseudoPk2 =
        buildPseudoverbalComplexObjectSentenceWithFallback(verbs, words, isPronominal = false, random = random, verbSentences = verbSentences)
    val pseudoPk2Trans = pseudoPk2.getTranslation(languageCode)
    val pseudoKeyboard2 = PhoneticKeyboardContent(
        sentence = pseudoPk2.hieroglyphs,
        info = "${pseudoPk2.transliteration} -> ${pseudoPk2Trans}",
        reading = pseudoPk2.transliteration.map { it.toString() },
        isProperName = false,
        canRotate = false
    )

    val allQuizzes = listOf(
        vsoWordBank,
        vsoTextTrans,
        advWordBank,
        advTextTrans,
        pseudoWordBank1,
        pseudoWordBank2,
        pseudoTextTrans1,
        pseudoTextTrans2,
        pseudoKeyboard1,
        pseudoKeyboard2
    )

    return allQuizzes.shuffled(random)
}
